import React, { useEffect, useRef, useState } from 'react';

export default function SolutionModal({
  isOpen,
  issue,
  selectedSolutionIndex = null,
  onClose,
  onUpload,
  onDeleteSolution,
  onGetDownloadUrl,
}) {
  const fileInputRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(selectedSolutionIndex);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  useEffect(() => {
    setActiveIndex(selectedSolutionIndex);
    setPreviewUrl('');
  }, [selectedSolutionIndex, issue?.id]);

  if (!isOpen || !issue) return null;

  const resolvedIssueId = issue?.issueId || issue?.id;

  const selectedSolution =
    activeIndex != null ? issue?.solutions?.[activeIndex] || null : null;

  const handleOpenPicker = () => {
    fileInputRef.current?.click();
  };

  const handleSelectSolution = (idx) => {
    setActiveIndex(idx);
    setPreviewUrl('');
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files?.length) {
      onUpload?.(resolvedIssueId, files);
    }
    e.target.value = '';
  };

  const handlePreview = async () => {
    if (!issue || activeIndex == null || !onGetDownloadUrl) return;

    const solution = issue?.solutions?.[activeIndex];
    if (!solution?.solutionId) return;

    try {
      setIsLoadingPreview(true);
      const res = await onGetDownloadUrl(issue, solution);
      setPreviewUrl(res?.downloadUrl || '');
    } catch (err) {
      console.error('Failed to get solution preview URL:', err);
      setPreviewUrl('');
      alert('Failed to load preview.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleOpenInNewTab = async () => {
    if (!issue || activeIndex == null || !onGetDownloadUrl) return;

    const solution = issue?.solutions?.[activeIndex];
    if (!solution?.solutionId) return;

    try {
      const res = await onGetDownloadUrl(issue, solution);
      if (res?.downloadUrl) {
        window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Could not open file.');
      }
    } catch (err) {
      console.error('Failed to get solution URL:', err);
      alert('Failed to open file.');
    }
  };

  const renderPreview = () => {
    if (!selectedSolution) {
      return <div style={{ color: '#6b7280' }}>Select a solution to preview.</div>;
    }

    if (isLoadingPreview) {
      return <div style={{ color: '#6b7280' }}>Loading preview...</div>;
    }

    if (!previewUrl) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><strong>Name:</strong> {selectedSolution.name}</div>
          <div><strong>Type:</strong> {selectedSolution.type || 'Unknown'}</div>
          <div><strong>Size:</strong> {selectedSolution.size ? `${selectedSolution.size} bytes` : 'Unknown'}</div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="table-action-btn btn-view" onClick={handlePreview}>
              Preview
            </button>
            <button type="button" className="table-action-btn btn-view" onClick={handleOpenInNewTab}>
              Open
            </button>
          </div>
        </div>
      );
    }

    if (selectedSolution.type?.startsWith('image/')) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <img
            src={previewUrl}
            alt={selectedSolution.name}
            style={{ maxWidth: '100%', borderRadius: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="table-action-btn btn-view" onClick={handleOpenInNewTab}>
              Open in New Tab
            </button>
          </div>
        </div>
      );
    }

    if (selectedSolution.type === 'application/pdf') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <embed
            src={previewUrl}
            type="application/pdf"
            style={{ width: '100%', height: '500px' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="table-action-btn btn-view" onClick={handleOpenInNewTab}>
              Open in New Tab
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div><strong>Name:</strong> {selectedSolution.name}</div>
        <div><strong>Type:</strong> {selectedSolution.type || 'Unknown'}</div>
        <div><strong>Size:</strong> {selectedSolution.size ? `${selectedSolution.size} bytes` : 'Unknown'}</div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={handleOpenInNewTab}>
            Open File
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="solution-modal-backdrop" onClick={onClose}>
      <div className="solution-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="solution-modal-header">
          <h3 style={{ margin: 0 }}>Solutions — {issue.ncCode}</h3>
          <button
            type="button"
            className="solution-modal-close"
            onClick={onClose}
          >
            x
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>
          <div>
            <button type="button" className="btn-add-solution" onClick={handleOpenPicker}>
              Add Solution
            </button>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.dwg,.dxf,.doc,.docx"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(issue.solutions || []).length === 0 && (
                <div style={{ color: '#6b7280' }}>No solutions uploaded yet.</div>
              )}

              {(issue.solutions || []).map((sol, idx) => (
                <div
                  key={sol.solutionId || `${sol.name}-${idx}`}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 10,
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleSelectSolution(idx)}
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: activeIndex === idx ? 700 : 500,
                    }}
                  >
                    {sol.name}
                  </button>

                  <button
                    type="button"
                    className="table-action-btn btn-delete"
                    onClick={() => onDeleteSolution?.(resolvedIssueId, idx)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, minHeight: 300 }}>
            {renderPreview()}
          </div>
        </div>
      </div>
    </div>
  );
}